import type { CommunicationRecord, PartnerOrganization } from "@entities";
import AssignmentIcon from "@mui/icons-material/Assignment";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { Avatar, Button, Chip, Stack, Typography } from "@mui/material";
import { sameOriginImageURL } from "@shared/helpers/constants";
import { type ColumnProps } from "@shared/frontend/hyper-table/types";
import { CommunicationRecordsTableCell } from "../../../components/CommunicationRecordManager/CommunicationRecordsTableCell";
import { TextPreviewPopover } from "../../../components/text-preview/TextPreviewPopover";
import {
  cooperationLevelChipColor,
  cooperationLevelShortLabel,
} from "./cooperation-level";
import { partnerOrgHandledByLabel } from "@shared/helpers/partner-organization-handled-by";
import {
  formatPartnerOrgActivityDate,
  formatPartnerOrgFollowUpDate,
  type PartnerOrganizationActivity,
} from "./partner-organization-activity";

export const buildPartnerOrganizationColumns = (
  onViewOpportunities: (organization: PartnerOrganization) => void,
  opportunityCounts: Record<string, number> = {},
  activityByOrgId: Record<string, PartnerOrganizationActivity> = {},
  onCommunicationSaved?: (
    organizationId: string,
    records?: CommunicationRecord[],
  ) => void,
): ColumnProps<PartnerOrganization>[] => [
  {
    field: "name",
    caption: "ארגון",
    enableSorting: true,
    minSize: 260,
    cell: ({ row }) => {
      const org = row.original;
      const logoId = (org.logoImageId || "").trim();
      const addresses = [...(org.addresses ?? [])]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((item) => item.address)
        .filter(Boolean);
      const addressLine =
        addresses.slice(0, 2).join(" · ") || org.address || "";
      const extraCount = Math.max(0, addresses.length - 2);
      return (
        <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
          <Avatar
            src={logoId ? sameOriginImageURL(logoId) : undefined}
            alt={org.name}
            imgProps={{ referrerPolicy: "no-referrer" }}
            sx={{ width: 40, height: 40 }}
          />
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              {org.name}
            </Typography>
            {org.dailyVolunteerTargetReached ? (
              <Chip size="small" color="warning" label="היעד היומי הושלם" />
            ) : null}
            {org.parent?.name ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                תחת {org.parent.name}
              </Typography>
            ) : null}
            {addressLine ? (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                title={addresses.join(" · ") || org.address || ""}
              >
                {addressLine}
                {extraCount > 0 ? ` +${extraCount}` : ""}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      );
    },
  },
  {
    field: "acceptsGroups",
    caption: "יכולות",
    enableSorting: false,
    minSize: 200,
    cell: ({ row }) => {
      const chips: string[] = [];
      if (row.original.acceptsGroups) chips.push("קבוצות");
      if (row.original.isAccessible) chips.push("נגישות");
      if (row.original.acceptsEnglishSpeakers) chips.push("אנגלית");
      if (chips.length === 0) return "-";
      return (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
          {chips.map((label) => (
            <Chip key={label} size="small" label={label} />
          ))}
        </Stack>
      );
    },
  },
  {
    field: "cooperationLevel",
    caption: "רצינות",
    minSize: 110,
    cell: ({ row }) => {
      const label = cooperationLevelShortLabel(row.original.cooperationLevel);
      if (!label) return "-";
      return (
        <Chip
          size="small"
          label={label}
          color={cooperationLevelChipColor(row.original.cooperationLevel)}
        />
      );
    },
  },
  {
    field: "comments",
    caption: "הערות פנימיות",
    cell: ({ row }) => (
      <TextPreviewPopover
        text={row.original.comments ?? ""}
        icon={<StickyNote2OutlinedIcon fontSize="small" />}
      />
    ),
    preventRowClick: true,
    minSize: 120,
    size: 150,
  },
  {
    field: "handledBy",
    caption: "מטפל",
    minSize: 110,
    cell: ({ row }) => {
      const label = partnerOrgHandledByLabel(row.original.handledBy);
      if (!label) return "-";
      return (
        <Chip
          size="small"
          label={label}
          color={
            row.original.handledBy === "CALL_CENTER" ? "primary" : "secondary"
          }
        />
      );
    },
  },
  {
    field: "updatedAt",
    caption: "פעילות",
    enableSorting: false,
    minSize: 180,
    deps: [activityByOrgId],
    cell: ({ row }) => {
      const activity = activityByOrgId[row.original.id];
      return (
        <Stack sx={{ py: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            כניסה: {formatPartnerOrgActivityDate(activity?.lastLoginAt)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            צורך: {formatPartnerOrgActivityDate(activity?.lastNeedAt)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            חזרה: {formatPartnerOrgFollowUpDate(row.original.nextFollowUpAt)}
          </Typography>
        </Stack>
      );
    },
  },
  {
    field: "communicationRecords",
    caption: "תיעוד תקשורת",
    minSize: 170,
    enableSorting: false,
    preventRowClick: true,
    deps: [activityByOrgId],
    cell: ({ row }) => {
      const activity = activityByOrgId[row.original.id];
      return (
        <CommunicationRecordsTableCell
          records={activity?.lastCommunications}
          title={row.original.name}
          partnerOrganizationId={row.original.id}
          onSave={(records) =>
            onCommunicationSaved?.(row.original.id, records)
          }
        />
      );
    },
  },
  {
    field: "handlingManager",
    caption: "אחראי",
    minSize: 140,
    enableSorting: false,
    cell: ({ row }) => {
      const name = row.original.handlingManager?.name?.trim();
      if (!name) return "-";
      return <Chip size="small" label={name} variant="outlined" />;
    },
  },
  {
    field: "opportunities",
    caption: "משימות",
    minSize: 120,
    enableSorting: false,
    deps: [opportunityCounts],
    cell: ({ row }) => {
      const count = opportunityCounts[row.original.id] ?? 0;
      return (
        <Button
          size="small"
          variant="text"
          startIcon={<AssignmentIcon fontSize="small" />}
          onClick={(e) => {
            e.stopPropagation();
            onViewOpportunities(row.original);
          }}
          sx={{ minWidth: 0, px: 0.5, flexShrink: 0 }}
          aria-label={`צפייה ב-${count} משימות של ${row.original.name}`}
        >
          {count}
        </Button>
      );
    },
  },
];
